import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { GET_ALL_PAYMENTS_ACTION, getAllPaymnets } from '~/actions';
import { useCheckPaymentExpiration } from '~/components/Inbox/Notification/Reminders/hooks/useCheckPaymentExpiration';
import { useAppSelector } from '~/hooks/useAppSelector';
import useLoading from '~/hooks/useLoading';

/**
 * Returns whether the effective office can pay — i.e. it has a valid (non-expired)
 * selected payment method (bank accounts are always treated as valid), is pre-approved,
 * or is a parent office. Single source of truth for gating value-added features
 * (invite / auto-confirm) that require payment.
 *
 * Delegation, pre-approval, parent-office and bank-account handling are inherited from
 * `useCheckPaymentExpiration`.
 *
 * Validity is derived from `state.payments.allPayments`, but several entry points into
 * the redesigned shift-request flow never fetch payments. Landing on such a page before
 * any payment-loading screen has run would leave the slice empty and falsely gate the
 * office as having no payment method (GOTU-3677). To avoid that, this hook fetches
 * payments on mount when they have not been loaded yet, and reports the office as able
 * to pay while that load is pending so no false "no payment method" gate is shown. The
 * submission-time 402 popup remains the safety net for the genuine no-payment case.
 */
export const useHasValidPaymentMethod = (officeId?: string): boolean => {
  const dispatch = useDispatch();
  const paymentsLoaded = useAppSelector((state) => state.payments.paymentsLoaded);
  const { isLoading } = useLoading([GET_ALL_PAYMENTS_ACTION]);
  const { isInvalidPayment } = useCheckPaymentExpiration(officeId);

  useEffect(() => {
    if (!paymentsLoaded && !isLoading) {
      dispatch(getAllPaymnets(officeId));
    }
  }, [dispatch, officeId, paymentsLoaded, isLoading]);

  // Treat the office as able to pay until payments have been loaded so the gate never
  // flashes a false lock while the fetch is still pending.
  if (!paymentsLoaded) return true;

  return !isInvalidPayment;
};
